import { SITE_TAGLINE } from '../config/siteTagline';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import Modal from './common/Modal';
import { BRAND_MARK_SRC } from '../config/brandMark.generated';
import { INVESTOR_BRIEF_FEATURES, INVESTOR_BRIEF_URL } from '../config/investorBrief';
import { SITE_FOOTER_EXTRAS, SITE_NAV_GROUPS, type SiteNavGroupId, type SiteNavItem } from '../config/siteNavigation';
import { useChatBarReach } from '../utils/chatBarReach';
import { landOn } from '../utils/sectionLanding';
import { useDemoChooser, type DemoChooserEntry } from './demoEntry/useDemoChooser';

// The foot is at least as tall as the closed chat bar reaches (src/utils/chatBarReach.ts; Miles, 2026-10-07), so at the
// very bottom of a page the bar never covers the footer's last line. The value is set on this element itself.
const FooterWrapper = styled.footer`
  width: 100%;
  padding: 5rem 2rem max(7rem, var(--chat-bar-reserve, 0px));
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  color: #555;
  font-size: 0.9rem;
  position: relative;

  @media (max-width: 768px) {
    padding: 3.5rem 1.25rem max(5rem, var(--chat-bar-reserve, 0px));
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  /* One track per column: the brand, then the site's groups (product,
     solutions, ROI, pricing, company since 2026-10-15:
     src/config/siteNavigation.ts, the list the new design's header and
     footer read too). */
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr;
  gap: 2rem;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 3rem 2rem;

    & > *:first-child {
      grid-column: 1 / -1;
    }
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2.5rem 1.25rem;
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const FooterLogo = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  color: #222;
  margin-bottom: 1.2rem;
  letter-spacing: -0.03em;
  display: flex;
  align-items: center;
  gap: clamp(0.5rem, 1.5vw, 0.75rem);
  background: transparent;

  &::after {
    content: '';
    display: inline-block;
    width: 24px;
    height: 2px;
    background: linear-gradient(135deg, var(--brand-gold-alt), var(--brand-black));
    margin-left: 0.6rem;
    border-radius: 2px;
  }
`;

const FooterLogoImage = styled.img`
  width: clamp(36px, 6vw, 44px);
  height: clamp(36px, 6vw, 44px);
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0 6px 20px rgba(200, 167, 94, 0.15);
  background: linear-gradient(135deg, rgba(200, 167, 94, 0.08), rgba(26, 26, 26, 0.08));
`;

const FooterLogoText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0;
`;

const FooterInlineLogo = styled.img`
  display: inline-block;
  height: 1.1em;
  margin-left: -0.12em;
  margin-bottom: 0.18em;
  vertical-align: baseline;
  object-fit: contain;
  filter: drop-shadow(0 2px 6px rgba(200, 167, 94, 0.15));
`;

const FooterDescription = styled.p`
  color: #666;
  margin-bottom: 1.5rem;
  line-height: 1.6;
  max-width: 400px;
  text-transform: lowercase;

  /* The acronym is written "AI" in the source string; this only stops the
     global lowercase rule from flattening it. Never uppercase -- no CSS on
     this platform forces copy into caps (owner rule 2026-08-16, pinned by
     tests/policy/noUppercase.test.ts). Same idea as KeepCase for Prest0n.
     NOTE: no backticks in here. This comment lives inside a styled-components
     template literal, where a backtick ends the literal and breaks the build. */
  .ai-uppercase {
    text-transform: none;
  }
`;

const FooterTitle = styled.h4`
  font-weight: 600;
  color: #333;
  margin-bottom: 1.5rem;
  position: relative;
  display: inline-block;
  /* The words are written in the site's voice; names and acronyms keep their capitals (ROI, FAQ: Miles, 2026-10-15),
     so nothing here changes their case. */
  text-transform: none;

  &::after {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 0;
    width: 30px;
    height: 2px;
    background: linear-gradient(135deg, var(--brand-gold-alt), var(--brand-black));
    border-radius: 2px;
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FooterLink = styled.li`
  margin-bottom: 0.8rem;

  /* a place is a link; the live demo's chooser is a button that looks like one (it opens a dialog, it goes nowhere) */
  a,
  button {
    color: #666;
    background: none;
    border: 0;
    font: inherit;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    padding: 0.25rem 0;
    text-transform: none; /* the places' words as written: ROI and FAQ keep their capitals */

    &:hover {
      color: var(--brand-gold-alt);
      transform: translateX(3px);
    }

    svg {
      width: 16px;
      height: 16px;
      flex-shrink: 0; /* a place whose words wrap (methodology & assumptions) keeps its glyph whole */
      margin-right: 0.5rem;
      opacity: 0.7;
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const SocialIcon = styled.a`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #555;
  transition: all 0.3s ease;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    background: var(--brand-gold-alt);
    color: var(--brand-charcoal);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const FooterBottom = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 2rem auto 0;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
    margin-bottom: 0;
  }
`;

const Copyright = styled.div`
  color: var(--brand-text-muted);
  font-size: 0.875rem;
  text-transform: none; /* Override inherited lowercase from parent containers */
`;

const FooterBottomLinks = styled.div`
  display: flex;
  gap: 1.5rem;

  a {
    color: #666;
    text-decoration: none;
    font-size: 0.875rem;
    transition: color 0.2s ease;
    text-transform: lowercase;
    min-height: 44px;
    display: inline-flex;
    align-items: center;

    &:hover {
      color: var(--brand-gold-alt);
    }
  }

  @media (max-width: 768px) {
    gap: 1rem;
    flex-wrap: wrap;
    justify-content: center;
  }
`;

// Demo video modal (shared across all pages via GlobalFooter)
const fadeInModal = keyframes`from { opacity: 0; } to { opacity: 1; }`;

const DemoModalOverlay = styled.div<{ $isOpen: boolean; $mobileFs?: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.88);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  padding: 2rem;
  cursor: pointer;
  animation: ${fadeInModal} 0.2s ease-out;

  @media (max-width: 768px) {
    padding: ${({ $mobileFs }) => $mobileFs ? '0' : '1rem'};
    background: ${({ $mobileFs }) => $mobileFs ? '#000' : 'rgba(0, 0, 0, 0.88)'};
  }
`;

const DemoModalContent = styled.div<{ $mobileFs?: boolean }>`
  position: relative;
  width: 100%;
  max-width: 1100px;
  cursor: default;

  @media (max-width: 768px) {
    ${({ $mobileFs }) => $mobileFs ? `
      max-width: 100%;
      width: 100vw;
      height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
    ` : ''}
  }
`;

const DemoModalVideoWrap = styled.div<{ $mobileFs?: boolean }>`
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  border-radius: 12px;
  overflow: hidden;
  background: #0b0c0f;
  box-shadow: 0 20px 80px rgba(0, 0, 0, 0.5);

  video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 12px;
  }

  @media (max-width: 768px) {
    border-radius: ${({ $mobileFs }) => $mobileFs ? '0' : '8px'};
    box-shadow: ${({ $mobileFs }) => $mobileFs ? 'none' : '0 20px 80px rgba(0, 0, 0, 0.5)'};

    ${({ $mobileFs }) => $mobileFs ? `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) rotate(90deg);
      width: 100vh;
      height: 100vw;
      padding-top: 0;

      video {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: contain;
        border-radius: 0;
      }
    ` : `
      video { border-radius: 8px; }
    `}
  }
`;

const DemoModalCloseBtn = styled.button`
  position: absolute;
  top: -44px;
  right: 0;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 4px 8px;
  transition: color 0.2s;
  font-family: 'Inter', sans-serif;

  &:hover { color: #fff; }

  @media (max-width: 768px) {
    top: -36px;
    font-size: 1.3rem;
  }
`;

const DEMO_VIDEO_TIME_KEY = 'demo_video_current_time';

const footerSvg = (children: React.ReactNode) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

/**
 * Each place's glyph in the columns, by the place's id (the new design's footer draws the same ones from its own map,
 * shell/headerParts.tsx PLACE_ICON, which this file may not import: it rides the shell's chunk).
 */
const circle = <circle cx="12" cy="12" r="10" />;
const shield = <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />;
const helpIcon = footerSvg(<>{circle}<path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><path d="M12 17h.01" /></>);
const briefcase = footerSvg(<><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></>);
const trendUp = footerSvg(<><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></>);
const dollar = footerSvg(<><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></>);
const PLACE_ICON: Record<string, React.ReactNode> = {
  'how-it-works': footerSvg(<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />),
  // "see what's inside": the pointer of the header's action (the app to step into, not a video to watch).
  demo: footerSvg(<><path d="m3 3 7.07 16.97 2.51-7.39 7.39-2.51L3 3z" /><path d="m13 13 6 6" /></>),
  integrations: footerSvg(<><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></>),
  control: footerSvg(<>{shield}<circle cx="12" cy="11" r="2" /><path d="M12 13v3" /></>),
  security: footerSvg(<><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>),
  'whats-new': footerSvg(<><path d="m3 11 18-5v12L3 14v-3z" /><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" /></>),
  'solutions-law': footerSvg(<><path d="M12 3v18" /><path d="M5 7h14" /><path d="M5 7l-3 7a4 4 0 0 0 6 0z" /><path d="M19 7l-3 7a4 4 0 0 0 6 0z" /><path d="M8 21h8" /></>),
  'solutions-marketing': footerSvg(<><path d="M12 19l7-7 3 3-7 7-3-3z" /><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" /><path d="M2 2l7.586 7.586" /><circle cx="11" cy="11" r="2" /></>),
  'solutions-finance': footerSvg(<path d="M18 20V10M12 20V4M6 20v-6" />),
  'solutions-real-estate': footerSvg(<><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></>),
  'solutions-insurance': footerSvg(<><path d="M22 12a10 10 0 0 0-20 0z" /><path d="M12 12v8a2 2 0 0 0 4 0" /><path d="M12 2v1" /></>),
  'solutions-property-management': footerSvg(<><circle cx="7.5" cy="15.5" r="5.5" /><path d="M11.4 11.6L21 2" /><path d="M18 5l3 3" /><path d="M15 8l2 2" /></>),
  'solutions-business': briefcase,
  roi: trendUp,
  'roi-calculator': footerSvg(<><rect x="4" y="2" width="16" height="20" rx="2" /><path d="M8 6h8M8 11h.01M12 11h.01M16 11h.01M8 15h.01M12 15h.01M16 15h.01M8 19h8" /></>),
  'roi-methodology': footerSvg(<><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></>),
  'roi-data': footerSvg(<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><path d="M12 15V3" /></>),
  pricing: dollar,
  'rate-card': footerSvg(<><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><path d="M7 7h.01" /></>),
  capacity: footerSvg(<><path d="M21.21 15.89A10 10 0 1 1 8 2.83" /><path d="M22 12A10 10 0 0 0 12 2v10z" /></>),
  'pricing-faq': helpIcon,
  team: footerSvg(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>),
  trust: footerSvg(<>{shield}<path d="M9 12l2 2 4-4" /></>),
  faq: helpIcon,
  about: footerSvg(<>{circle}<path d="M12 16v-4" /><path d="M12 8h.01" /></>),
  mission: footerSvg(<><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" /></>),
  insights: footerSvg(<><line x1="12" y1="2" x2="12" y2="6" /><path d="M5 10a7 7 0 0 1 14 0c0 3.07-1.63 4.68-3 5.58a2 2 0 0 0-1 1.7V19a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v-1.72a2 2 0 0 0-1-1.7C6.63 14.68 5 13.07 5 10z" /><line x1="9" y1="22" x2="15" y2="22" /></>),
  referrals: footerSvg(<><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" /><polyline points="16 6 12 2 8 6" /><line x1="12" y1="2" x2="12" y2="15" /></>),
};

const footerGroup = (id: SiteNavGroupId) => SITE_NAV_GROUPS.find((g) => g.id === id) as (typeof SITE_NAV_GROUPS)[number];

/**
 * A place in the columns. A link to a section of a page (`/#faq`, `/pricing#faq`), pressed on that page, scrolls there
 * itself, landing it in the middle of the screen (src/utils/sectionLanding.ts); from anywhere else the router carries
 * the hash.
 */
const footerPlace = (inside: DemoChooserEntry | null) => (p: SiteNavItem) => (
  <FooterLink key={p.id}>
    {p.opens === 'inside' && inside ? (
      // The way inside prest0 (the public demo): the industry chooser's action, where the lever lets one in.
      <button type="button" aria-haspopup="dialog" onClick={inside.open} onPointerEnter={inside.reach} onFocus={inside.reach}>
        {PLACE_ICON[p.id]}
        {p.label}
      </button>
    ) : (
    <Link
      to={p.to}
      onClick={p.to.includes('#') ? () => {
        if (window.location.pathname === p.to.slice(0, p.to.indexOf('#'))) {
          setTimeout(() => {
            const el = document.getElementById(p.to.slice(p.to.indexOf('#') + 1));
            if (el) landOn(el);
          }, 50);
        }
      } : undefined}
    >
      {PLACE_ICON[p.id]}
      {p.label}
    </Link>
    )}
  </FooterLink>
);

/** A group's places, then the pages the footers keep beyond the menus (SITE_FOOTER_EXTRAS). */
const footerPlaces = (id: SiteNavGroupId, inside: DemoChooserEntry | null) =>
  [...footerGroup(id).items, ...(SITE_FOOTER_EXTRAS[id] || [])].map(footerPlace(inside));

const GlobalFooter: React.FC = () => {
  // The way inside prest0 (the public demo, exception M1): the product column's "see what's inside" opens the industry
  // chooser where the lever lets one in (one entry, from the site's list; 2026-11-02 it left the company column).
  const liveDemo = useDemoChooser();
  const [demoOpen, setDemoOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isMobileFullscreen, setIsMobileFullscreen] = useState(false);
  const demoVideoRef = useRef<HTMLVideoElement>(null);

  // Detect mobile viewport
  const isMobileRef = useRef(typeof window !== 'undefined' && window.matchMedia('(max-width: 820px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 820px)');
    const handler = (e: MediaQueryListEvent) => { isMobileRef.current = e.matches; };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const closeDemo = useCallback(() => {
    // Save current playback position before closing
    if (demoVideoRef.current && demoVideoRef.current.currentTime > 0) {
      try { sessionStorage.setItem(DEMO_VIDEO_TIME_KEY, String(demoVideoRef.current.currentTime)); } catch {}
    }
    setDemoOpen(false);
    if (demoVideoRef.current) demoVideoRef.current.pause();
    // Exit fullscreen if active
    if (document.fullscreenElement || (document as any).webkitFullscreenElement) {
      try { document.exitFullscreen().catch(() => {}); } catch {}
    }
    setIsMobileFullscreen(false);
    try {
      const orientation = (window.screen as any).orientation;
      if (orientation && orientation.unlock) orientation.unlock();
    } catch {}
  }, []);

  // Auto-play + resume position + mobile fullscreen when modal opens
  useEffect(() => {
    if (!demoOpen) return;
    const timer = setTimeout(() => {
      const video = demoVideoRef.current;
      if (!video) return;

      const restoreAndPlay = () => {
        // Pause ALL other video elements first (single-instance guarantee)
        document.querySelectorAll('video').forEach(v => {
          if (v !== video && !v.paused) v.pause();
        });

        try {
          const saved = sessionStorage.getItem(DEMO_VIDEO_TIME_KEY);
          if (saved) {
            const t = parseFloat(saved);
            if (t > 0 && isFinite(t) && t < (video.duration || Infinity)) {
              video.currentTime = t;
            }
          }
        } catch {}

        video.play().catch(() => {});
      };

      // Force the browser to start loading (hidden videos may not load automatically)
      if (video.readyState < 1) {
        video.load();
      }

      if (video.readyState >= 3) {
        restoreAndPlay();
      } else {
        // Listen for canplay which fires once enough data is buffered to start playback
        video.addEventListener('canplay', restoreAndPlay, { once: true });
        // Fallback: if canplay doesn't fire within 1s, try playing anyway
        setTimeout(() => {
          if (video.paused) {
            restoreAndPlay();
          }
        }, 1000);
      }

      // Mobile fullscreen
      if (isMobileRef.current) {
        (async () => {
          try {
            const fsTarget = video as any;
            const reqFS = fsTarget.requestFullscreen || fsTarget.webkitRequestFullscreen || fsTarget.webkitEnterFullScreen || fsTarget.mozRequestFullScreen;
            if (reqFS) {
              await reqFS.call(fsTarget);
              setIsMobileFullscreen(true);
              try {
                const orientation = (window.screen as any).orientation;
                if (orientation?.lock) await orientation.lock('landscape').catch(() => {});
              } catch {}
            }
          } catch {
            setIsMobileFullscreen(true);
          }
        })();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [demoOpen]);

  // Listen for fullscreen exit
  useEffect(() => {
    const handler = () => {
      if (!document.fullscreenElement && !(document as any).webkitFullscreenElement) {
        if (isMobileFullscreen) {
          setIsMobileFullscreen(false);
          try { const o = (window.screen as any).orientation; if (o?.unlock) o.unlock(); } catch {}
        }
        if (demoOpen && isMobileRef.current) closeDemo();
      }
    };
    document.addEventListener('fullscreenchange', handler);
    document.addEventListener('webkitfullscreenchange', handler);
    return () => {
      document.removeEventListener('fullscreenchange', handler);
      document.removeEventListener('webkitfullscreenchange', handler);
    };
  }, [isMobileFullscreen, demoOpen, closeDemo]);

  // Close on Escape key
  useEffect(() => {
    if (!demoOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeDemo(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [demoOpen, closeDemo]);

  const handleDemoClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setDemoOpen(true);
  };

  const currentYear = new Date().getFullYear();
  const chatBarReach = useChatBarReach();

  return (
    <>
    <FooterWrapper data-site-footer="" data-link-landing="land" style={chatBarReach > 0 ? ({ '--chat-bar-reserve': `${chatBarReach}px` } as React.CSSProperties) : undefined}>
      <FooterContent>
        <FooterColumn>
          <FooterLogo>
            <FooterLogoImage src={BRAND_MARK_SRC} alt="prest0 logo" />
            <FooterLogoText>prest<FooterInlineLogo src={BRAND_MARK_SRC} alt="0" /></FooterLogoText>
          </FooterLogo>
          <FooterDescription>{SITE_TAGLINE}</FooterDescription>
          <SocialLinks>
            <SocialIcon
              href="https://www.youtube.com/channel/UCeSWFg_9_RYF7dSbWItxzxA"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z" />
                <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" />
              </svg>
            </SocialIcon>
            <SocialIcon
              href="https://x.com/milesprojolivia"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="X"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </SocialIcon>
            <SocialIcon
              href="https://www.linkedin.com/company/prest0"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                <rect x="2" y="9" width="4" height="12" />
                <circle cx="4" cy="4" r="2" />
              </svg>
            </SocialIcon>
          </SocialLinks>
        </FooterColumn>

        <FooterColumn>
          <FooterTitle>{footerGroup('product').label}</FooterTitle>
          <FooterLinks>{footerPlaces('product', liveDemo)}</FooterLinks>
        </FooterColumn>

        <FooterColumn>
          <FooterTitle>{footerGroup('solutions').label}</FooterTitle>
          <FooterLinks>{footerPlaces('solutions', liveDemo)}</FooterLinks>
        </FooterColumn>

        <FooterColumn>
          <FooterTitle>{footerGroup('roi').label}</FooterTitle>
          <FooterLinks>{footerPlaces('roi', liveDemo)}</FooterLinks>
        </FooterColumn>

        <FooterColumn>
          <FooterTitle>{footerGroup('pricing').label}</FooterTitle>
          <FooterLinks>{footerPlaces('pricing', liveDemo)}</FooterLinks>
        </FooterColumn>

        <FooterColumn>
          <FooterTitle>{footerGroup('company').label}</FooterTitle>
          <FooterLinks>
            {footerPlaces('company', liveDemo)}
            <FooterLink>
              <a href="#" onClick={(e) => { 
                e.preventDefault(); 
                window.open(INVESTOR_BRIEF_URL, 'InvestorBrief', INVESTOR_BRIEF_FEATURES);
              }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2v6a2 2 0 0 0 2 2h6" />
                  <path d="M5 14H4a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2h-1" />
                  <path d="M5 14V5a2 2 0 0 1 2-2h7l5 5v6Z" />
                  <path d="M10 10v4" />
                  <path d="M14 10v4" />
                  <path d="M8 10v4" />
                </svg>
                for investors
              </a>
            </FooterLink>
            <FooterLink>
              <a href="#" onClick={handleDemoClick}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                demo
              </a>
            </FooterLink>
          </FooterLinks>
        </FooterColumn>
      </FooterContent>

      <FooterBottom>
        <Copyright>© {currentYear} Prest0 Inc. all rights reserved.</Copyright>
        <FooterBottomLinks>
          <Link to="/privacy-policy">privacy policy</Link>
          <Link to="/terms-of-service">terms of service</Link>
          <Link to="/cookie-policy">cookie policy</Link>
          {/* The Trust Center (2026-09-11: the subprocessor list is only a disclosure if a reader can find it without
              being told) is the company column's "trust & data handling" since 2026-10-19, one link, not two; the
              sitemap is for crawlers, which robots.txt sends to it. */}
        </FooterBottomLinks>
      </FooterBottom>
    </FooterWrapper>

    <DemoModalOverlay $isOpen={demoOpen} $mobileFs={isMobileFullscreen && isMobileRef.current} onClick={closeDemo}>
      <DemoModalContent $mobileFs={isMobileFullscreen && isMobileRef.current} onClick={(e) => e.stopPropagation()}>
        <DemoModalCloseBtn onClick={closeDemo}>✕</DemoModalCloseBtn>
        <DemoModalVideoWrap $mobileFs={isMobileFullscreen && isMobileRef.current}>
          <video
            ref={demoVideoRef}
            src="https://storage.googleapis.com/project-olivia-frontend-415/demo-video/prest0-demo-latest.mp4"
            controls
            playsInline
            /* preload="none": the open-effect calls video.load() when
               readyState < 1, so playback is unaffected — but the closed
               modal no longer fetches metadata of a 44 MB mp4 on every
               page that renders the footer (incl. the home page). */
            preload="none"
            onEnded={() => {
              closeDemo();
              setIsBookingModalOpen(true);
            }}
          />
        </DemoModalVideoWrap>
      </DemoModalContent>
    </DemoModalOverlay>
    <Modal
      isOpen={isBookingModalOpen}
      onClose={() => setIsBookingModalOpen(false)}
      title="book a meeting with the team"
      size="xlarge"
      maxWidth="1100px"
      footer={null}
      noScroll={true}
    >
      <div style={{ height: 'calc(90vh - 80px)', minHeight: '600px', width: '100%', borderRadius: '0 0 12px 12px', overflow: 'hidden' }}>
        <iframe
          src="https://calendar.google.com/calendar/appointments/schedules/AcZssZ2bVpuAMuVJh3Xikmu1Bi9AuBaaHxB5a-HeOlE8xm3l2ehjgrdvG5QQEqmrLDKsF1Vgbncg2BIK?gv=true"
          style={{ border: 'none', width: '100%', height: '100%' }}
        ></iframe>
      </div>
    </Modal>
    {liveDemo?.dialog}
    </>
  );
};

export default GlobalFooter;
